import { Directive, HostListener, inject, Input, Output, TemplateRef, Type, ViewChild } from '@angular/core';

import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable, Subject, take } from 'rxjs';

import { Router } from '@angular/router';
import { ServiceLocator } from '@summit/core';
import { SummitConfirmConfig } from '@summit/models';
import { LayoutService } from '@summit/ui';
import { ConfirmationAddPrimeComponent } from '../components/confirmation-add-prime/confirmation-add.component';
import { PrimeConfirmationComponent } from '../components/confirmation-prime';
import { DeleteConfirmationPrimeComponent } from '../components/delete-confirmation-prime/delete-confirmation.component';
import { DialogEditConfig, DialogSelectConfig } from '../models';

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[confirm]',
  providers: [DialogService],
  standalone: false
})
export class SummitDialogDirective {
  @Input() emitOrigin?: any;
  @Input() confirmData?: any;
  @ViewChild(TemplateRef) tpl: TemplateRef<unknown>;
  @Output() confirm = new Subject<void>();

  ghostClick = false;
  dialogService: DialogService;
  router: Router;
  layout: LayoutService;

  constructor() {
    //inject doesn't work here for time-off-edit delete
    try {
      this.dialogService = inject(DialogService);
    } catch {
      this.dialogService = ServiceLocator.get(DialogService);
    }
    try {
      this.router = inject(Router);
    } catch {
      this.router = ServiceLocator.get(Router);
    }
    try {
      this.layout = inject(LayoutService);
    } catch {
      this.layout = ServiceLocator.get(LayoutService);
    }
  }

  @HostListener('click', ['$event'])
  open(event: any) {
    //console.log('tttt', this.emitOrigin);
    if (!this.ghostClick) {
      //console.log('[event]', event);
      event.emitOption = this.emitOrigin?.emitOption ?? null;
      event.stopPropagation();
      event.preventDefault();
      const dynamicDialogConfig = {} as DynamicDialogConfig;
      dynamicDialogConfig.data = this.confirmData;
      this.confirmAction(
        dynamicDialogConfig,
        () => {
          //console.log('forwarding event');
          this.forwardEvent(event);
        },
        () => this.stopEvent(event)
      );
    } else {
      this.ghostClick = true;
    }
  }

  //Call from TypeScript
  confirmAction(
    confirmData: DynamicDialogConfig<SummitConfirmConfig>,
    successCallback: () => void,
    errorCallback?: () => void
  ) {
    confirmData.header ??= 'Please Confirm';
    this.openDialogAndWait(
      confirmData,
      (_data?) => {
        //console.log('bubbling up callback');
        successCallback();
      },
      () => {
        if (errorCallback) errorCallback();
      }
    );
  }

  //used to show a dialog or link to a page based on if the user is on mobile or not
  showOrLink<T>(
    component: Type<unknown>,
    confirmData: DynamicDialogConfig<DialogEditConfig<T> | DialogSelectConfig<T> | string | Observable<unknown>>,
    link: string | string[],
    queryParams: Record<string, unknown> = {},
    successCallback?: (data: T) => void,
    errorCallback?: () => void
  ) {
    if (this.layout.isMobile()) {
      const lclLink = Array.isArray(link) ? link : [link];
      this.router.navigate(lclLink, { queryParams: queryParams });
    } else {
      this.show(component, confirmData, successCallback, errorCallback);
    }
  }

  show<T>(
    component: Type<unknown>,
    confirmData: DynamicDialogConfig<DialogEditConfig<T> | DialogSelectConfig<T> | string | Observable<unknown>>,
    successCallback?: (data: T) => void,
    errorCallback?: () => void
  ) {
    //console.log('show', component, confirmData, successCallback, errorCallback);
    this.openDialogAndWait(confirmData, successCallback, errorCallback, component);
  }

  private async openDialogAndWait<T>(
    dialogConfig: DynamicDialogConfig<
      SummitConfirmConfig | DialogEditConfig<T> | DialogSelectConfig<T> | string | Observable<unknown>
    >,
    successCallback?: (data: T) => void,
    errorCallback?: () => void,
    component?: Type<unknown>
  ) {
    const config = { ...dialogConfig };
    if (config.header) config.showHeader = true;
    //Default some values
    //If you are troubleshooting, make sure you did not:
    //1.  Put a semicolon in the value field.
    config.style = { 'max-height': '98vh', 'max-width': '95vw', ...config.style };
    config.contentStyle = {
      'max-height': '80vh',
      'max-width': '95vw',
      overflow: 'auto',
      ...config.contentStyle
    };
    config.baseZIndex ??= 999;
    config.draggable = true;
    config.resizable ??= false;
    config.keepInViewport ??= true;
    config.modal ??= true;
    config.dismissableMask ??= true;
    config.focusTrap ??= true;
    config.focusOnShow ??= true;
    config.closable ??= true;
    let ref: DynamicDialogRef;
    //console.log('dynamic dialog config:', config);
    const confirmType = config.data?.['confirmType'];
    if (component) {
      ref = this.dialogService.open(component, config);
    } else {
      switch (confirmType) {
        case 'delete': {
          ref = this.dialogService.open(DeleteConfirmationPrimeComponent, config);
          break;
        }
        case 'add': {
          ref = this.dialogService.open(ConfirmationAddPrimeComponent, config);
          break;
        }
        default: {
          ref = this.dialogService.open(PrimeConfirmationComponent, config);
          break;
        }
      }
    }
    ref?.onClose.pipe(take(1)).subscribe((result) => {
      //console.log('result:', result);
      if (component) {
        if (result || result === 0 || result === false || result === null) {
          if (successCallback) {
            //console.log('calling successCallback');
            successCallback(result);
          }
        } else if (errorCallback) errorCallback();
      } else {
        //Confirm Box
        if (result) {
          if (successCallback) {
            //console.log('calling successCallback');
            successCallback(result);
          }
        } else if (errorCallback) errorCallback();
      }
    });
  }

  private forwardEvent(event: Event) {
    const confirmRef = this.confirm;
    confirmRef.next();
    this.ghostClick = true;
    const target = event.target;
    if (target && 'dispatchEvent' in target) {
      (target as EventTarget).dispatchEvent(event);
    }
  }

  click(event) {
    console.log(event);
  }
  stopEvent(event) {
    event.stopPropagation();
    event.preventDefault();
    console.log(event);
    //this.close();
  }

  close() {
    // this.dialogService();
  }
}
